import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';

const HealthHistoryForm = ({ clientId, data, onChange, onComplete }) => {
  const [formData, setFormData] = useState({
    medicalConditions: [],
    medications: '',
    allergies: '',
    surgeries: '',
    familyHistory: [],
    smokingStatus: '',
    alcoholConsumption: '',
    sleepQuality: 5,
    stressLevel: 5,
    energyLevel: 5,
    chronicPain: false,
    painAreas: '',
    doctorClearance: false,
    emergencyContact: '',
    emergencyPhone: '',
    ...data
  });

  useEffect(() => {
    onChange(formData);
    checkCompletion();
  }, [formData]);

  const checkCompletion = () => {
    const required = ['smokingStatus', 'alcoholConsumption', 'emergencyContact', 'emergencyPhone'];
    const isComplete = required?.every(field => formData?.[field]?.toString()?.trim());
    onComplete(isComplete);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleConditionToggle = (condition) => {
    setFormData(prev => {
      const current = prev?.medicalConditions || [];
      const updated = current?.includes(condition)
        ? current?.filter(c => c !== condition)
        : [...current, condition];
      return { ...prev, medicalConditions: updated };
    });
  };

  const handleFamilyHistoryToggle = (condition) => {
    setFormData(prev => {
      const current = prev?.familyHistory || [];
      const updated = current?.includes(condition)
        ? current?.filter(c => c !== condition)
        : [...current, condition];
      return { ...prev, familyHistory: updated };
    });
  };

  const medicalConditionsList = [
    { value: 'diabetes', label: 'Diabetes', icon: 'Activity' },
    { value: 'hypertension', label: 'High Blood Pressure', icon: 'Heart' },
    { value: 'heart-disease', label: 'Heart Disease', icon: 'HeartPulse' },
    { value: 'asthma', label: 'Asthma', icon: 'Wind' },
    { value: 'arthritis', label: 'Arthritis', icon: 'Bone' },
    { value: 'thyroid', label: 'Thyroid Issues', icon: 'Pill' },
    { value: 'anxiety', label: 'Anxiety/Depression', icon: 'Brain' },
    { value: 'none', label: 'None', icon: 'CheckCircle' }
  ];

  const familyConditions = [
    { value: 'heart-disease', label: 'Heart Disease' },
    { value: 'diabetes', label: 'Diabetes' },
    { value: 'cancer', label: 'Cancer' },
    { value: 'obesity', label: 'Obesity' },
    { value: 'hypertension', label: 'Hypertension' },
    { value: 'none', label: 'None' }
  ];

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-foreground mb-2">Health History</h2>
        <p className="text-muted-foreground">Comprehensive medical background and current health status</p>
      </div>

      {/* Medical Conditions */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <Icon name="Heart" size={20} className="text-primary" />
          Current Medical Conditions
        </h3>
        <p className="text-sm text-muted-foreground">Select all that apply</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {medicalConditionsList?.map((condition) => (
            <button
              key={condition?.value}
              type="button"
              onClick={() => handleConditionToggle(condition?.value)}
              className={`
                p-4 rounded-xl border-2 transition-all hover:scale-105
                ${
                  formData?.medicalConditions?.includes(condition?.value)
                    ? 'border-primary bg-primary/10 shadow-lg'
                    : 'border-border hover:border-primary/50 bg-background'
                }
              `}
            >
              <div className="flex flex-col items-center gap-2">
                <div className={`
                  w-10 h-10 rounded-lg flex items-center justify-center
                  ${formData?.medicalConditions?.includes(condition?.value) ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'}
                `}>
                  <Icon name={condition?.icon} size={20} />
                </div>
                <span className="text-xs font-medium text-foreground text-center">{condition?.label}</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Medications & Allergies */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <Icon name="Pill" size={20} className="text-primary" />
          Medications & Allergies
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Current Medications
            </label>
            <textarea
              name="medications"
              value={formData?.medications}
              onChange={handleInputChange}
              className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:ring-2 focus:ring-ring"
              rows={3}
              placeholder="List all current medications and dosages..."
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Known Allergies
            </label>
            <textarea
              name="allergies"
              value={formData?.allergies}
              onChange={handleInputChange}
              className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:ring-2 focus:ring-ring"
              rows={3}
              placeholder="List any allergies (medications, foods, etc.)..."
            />
          </div>
        </div>
      </div>

      {/* Surgical History */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <Icon name="Stethoscope" size={20} className="text-primary" />
          Surgical History
        </h3>
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Previous Surgeries or Procedures
          </label>
          <textarea
            name="surgeries"
            value={formData?.surgeries}
            onChange={handleInputChange}
            className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:ring-2 focus:ring-ring"
            rows={3}
            placeholder="List any surgeries or major medical procedures with approximate dates..."
          />
        </div>
      </div>

      {/* Family History */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <Icon name="Users" size={20} className="text-primary" />
          Family Medical History
        </h3>
        <p className="text-sm text-muted-foreground">Select conditions present in immediate family</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {familyConditions?.map((condition) => (
            <button
              key={condition?.value}
              type="button"
              onClick={() => handleFamilyHistoryToggle(condition?.value)}
              className={`
                p-3 rounded-lg border-2 transition-all text-left hover:scale-[1.02]
                ${
                  formData?.familyHistory?.includes(condition?.value)
                    ? 'border-primary bg-primary/10' :'border-border hover:border-primary/50 bg-background'
                }
              `}
            >
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-foreground">{condition?.label}</span>
                {formData?.familyHistory?.includes(condition?.value) && (
                  <Icon name="Check" size={16} className="text-primary" />
                )}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Lifestyle Factors */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <Icon name="Coffee" size={20} className="text-primary" />
          Lifestyle Factors
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Smoking Status
            </label>
            <select
              name="smokingStatus"
              value={formData?.smokingStatus}
              onChange={handleInputChange}
              className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:ring-2 focus:ring-ring"
            >
              <option value="">Select status...</option>
              <option value="never">Never smoked</option>
              <option value="former">Former smoker</option>
              <option value="current">Current smoker</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Alcohol Consumption
            </label>
            <select
              name="alcoholConsumption"
              value={formData?.alcoholConsumption}
              onChange={handleInputChange}
              className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:ring-2 focus:ring-ring"
            >
              <option value="">Select frequency...</option>
              <option value="none">None</option>
              <option value="occasional">Occasional (1-2/week)</option>
              <option value="moderate">Moderate (3-5/week)</option>
              <option value="frequent">Frequent (daily)</option>
            </select>
          </div>
        </div>
      </div>

      {/* Wellness Metrics */}
      <div className="space-y-6">
        <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <Icon name="Activity" size={20} className="text-primary" />
          Current Wellness Status
        </h3>
        
        <div className="space-y-4">
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-foreground">Sleep Quality</label>
              <span className="text-lg font-bold text-primary">{formData?.sleepQuality}/10</span>
            </div>
            <input
              type="range"
              name="sleepQuality"
              min="1"
              max="10"
              value={formData?.sleepQuality}
              onChange={handleInputChange}
              className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer"
            />
            <div className="flex justify-between text-xs text-muted-foreground mt-1">
              <span>Poor</span>
              <span>Excellent</span>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-foreground">Stress Level</label>
              <span className="text-lg font-bold text-primary">{formData?.stressLevel}/10</span>
            </div>
            <input
              type="range"
              name="stressLevel"
              min="1"
              max="10"
              value={formData?.stressLevel}
              onChange={handleInputChange}
              className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer"
            />
            <div className="flex justify-between text-xs text-muted-foreground mt-1">
              <span>Low</span>
              <span>High</span>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-foreground">Energy Level</label>
              <span className="text-lg font-bold text-primary">{formData?.energyLevel}/10</span>
            </div>
            <input
              type="range"
              name="energyLevel"
              min="1"
              max="10"
              value={formData?.energyLevel}
              onChange={handleInputChange}
              className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer"
            />
            <div className="flex justify-between text-xs text-muted-foreground mt-1">
              <span>Low</span>
              <span>High</span>
            </div>
          </div>
        </div>
      </div>

      {/* Pain Assessment */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <Icon name="AlertCircle" size={20} className="text-primary" />
          Pain Assessment
        </h3>
        <div className="flex items-center space-x-3 p-4 bg-background rounded-lg border border-border">
          <input
            type="checkbox"
            id="chronicPain"
            name="chronicPain"
            checked={formData?.chronicPain}
            onChange={handleInputChange}
            className="w-5 h-5 text-primary bg-background border-border rounded focus:ring-2 focus:ring-ring"
          />
          <label htmlFor="chronicPain" className="text-sm font-medium text-foreground">
            I experience chronic pain or discomfort
          </label>
        </div>
        {formData?.chronicPain && (
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Describe pain areas and severity
            </label>
            <textarea
              name="painAreas"
              value={formData?.painAreas}
              onChange={handleInputChange}
              className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:ring-2 focus:ring-ring"
              rows={3}
              placeholder="Describe location, frequency, and intensity of pain..."
            />
          </div>
        )}
      </div>

      {/* Emergency Contact */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <Icon name="Phone" size={20} className="text-primary" />
          Emergency Contact
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Emergency Contact Name"
            name="emergencyContact"
            value={formData?.emergencyContact}
            onChange={handleInputChange}
            placeholder="Full name"
            required
          />
          <Input
            label="Emergency Contact Phone"
            name="emergencyPhone"
            type="tel"
            value={formData?.emergencyPhone}
            onChange={handleInputChange}
            placeholder="Phone number"
            required
          />
        </div>
      </div>

      {/* Doctor Clearance */}
      <div className="space-y-4">
        <div className="p-4 bg-secondary/10 border border-secondary rounded-lg">
          <div className="flex items-start space-x-3">
            <input
              type="checkbox"
              id="doctorClearance"
              name="doctorClearance"
              checked={formData?.doctorClearance}
              onChange={handleInputChange}
              className="mt-1 w-5 h-5 text-primary bg-background border-border rounded focus:ring-2 focus:ring-ring"
            />
            <label htmlFor="doctorClearance" className="text-sm text-foreground">
              <span className="font-semibold">Medical Clearance:</span> I confirm that I have consulted with my healthcare provider and have been cleared to participate in physical exercise, or I do not have any medical conditions that would require such clearance.
            </label>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HealthHistoryForm;